import type { KeyedMutator } from "swr";

import type { UiEvents } from "@keystone-ui/types";

import type { KeystoneApiClient } from "data/keystone-api-client";
import { type NotificationData } from "data/keystone-api-mapping";
import { isError } from "data/ss-error";
import { logException } from "utils";

export type NotificationState = {
  read: string[];
  dismissed: string[];
};

export type NotificationUpdate = Partial<NotificationState>;

type NotificationEvents = NotificationData["notifications"];

const EMPTY_UI_EVENTS: UiEvents = { updated: "", events: {} };

// ui-events is loosely typed; notification state lives under
// events.notifications, keyed by policy id.
const notificationEvents = (uiEvents: UiEvents | undefined) =>
  uiEvents?.events?.notifications as NotificationEvents | undefined;

export const getNotificationState = (
  uiEvents: UiEvents | undefined,
  policyId: string | undefined,
): NotificationState => {
  const policyEvents = policyId
    ? notificationEvents(uiEvents)?.[policyId]
    : undefined;

  return {
    read: policyEvents?.read ?? [],
    dismissed: policyEvents?.dismissed ?? [],
  };
};

const applyUpdate = (
  uiEvents: UiEvents,
  policyId: string,
  update: NotificationUpdate,
): UiEvents => {
  const current = getNotificationState(uiEvents, policyId);
  const events = {
    ...uiEvents.events,
    notifications: {
      ...notificationEvents(uiEvents),
      [policyId]: {
        read: [...new Set([...current.read, ...(update.read ?? [])])],
        dismissed: [
          ...new Set([...current.dismissed, ...(update.dismissed ?? [])]),
        ],
      },
    },
  };

  return { ...uiEvents, events: events as unknown as UiEvents["events"] };
};

type NotificationUpdateContext = {
  policyId: string;
  userId: string;
  keystone: KeystoneApiClient;
  mutateUiEvents: KeyedMutator<UiEvents>;
};

const writeUpdate = async (
  { policyId, userId, keystone, mutateUiEvents }: NotificationUpdateContext,
  update: NotificationUpdate,
): Promise<void> => {
  await mutateUiEvents(
    async (uiEvents) => {
      const updatedUiEvents = applyUpdate(
        uiEvents ?? EMPTY_UI_EVENTS,
        policyId,
        update,
      );
      const res = await keystone.updateUserNotifications(userId, {
        notifications: notificationEvents(updatedUiEvents) ?? {},
      });
      if (isError(res)) {
        throw res;
      }

      return updatedUiEvents;
    },
    {
      // Show the change immediately, before the PATCH resolves.
      optimisticData: (uiEvents) =>
        applyUpdate(uiEvents ?? EMPTY_UI_EVENTS, policyId, update),
      // Don't follow up with a GET to reconfirm: it could result in a race with the PATCH
      revalidate: false,
      rollbackOnError: true,
    },
  ).catch((err) => {
    logException(err);
  });
};

// The whole ui-events record is PATCHed as one document, so writes are
// chained: each payload is built from the previous write's result instead of
// racing on the same base and clobbering it.
let pendingWrites: Promise<unknown> = Promise.resolve();

export const updateNotifications = (
  context: {
    policyId: string | undefined;
    userId: string | undefined;
    keystone: KeystoneApiClient;
    mutateUiEvents: KeyedMutator<UiEvents>;
  },
  update: NotificationUpdate,
): Promise<void> => {
  const { policyId, userId } = context;
  const hasChanges = !!update.read?.length || !!update.dismissed?.length;
  if (!policyId || !userId || !hasChanges) {
    return Promise.resolve();
  }

  pendingWrites = pendingWrites
    .then(() => writeUpdate({ ...context, policyId, userId }, update))
    .catch(() => undefined);

  return pendingWrites as Promise<void>;
};
